import React, { useState, useMemo } from 'react';
import { SwimEvent, User, PaymentInfo } from '../types';
import { MapPin, Calendar, Users, Trophy, X, AlertCircle, ShieldAlert, CheckCircle2, Search, Clock } from 'lucide-react';

export interface RegisterFormData {
  eventId: number;
  athleteName: string;
  idCard: string;
  phone: string;
  gender: '男' | '女';
  ageGroup: string;
  eventItem: string;
  emergencyContact: string;
  emergencyPhone: string;
  tshirtSize: string;
  healthDeclared: boolean;
}

interface EventSectionProps {
  events: SwimEvent[];
  user: User | null;
  onRegister: (event: SwimEvent, form: RegisterFormData) => Promise<PaymentInfo>;
  onRequireLogin: () => void;
}

const CATEGORIES = ['全部', '大众普及赛', '青少年公开赛', '竞技挑战', '接力邀请赛'];
const AGE_GROUPS = ['少年组 (12-17岁)', '青年组 (18-35岁)', '中年组 (36-55岁)', '长青组 (56岁以上)'];
const EVENT_ITEMS = ['50米自由泳', '50米蛙泳', '100米自由泳', '100米蛙泳', '200米混合泳', '4x50米接力'];
const TSHIRT_SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];

export const EventSection: React.FC<EventSectionProps> = ({ events, user, onRegister, onRequireLogin }) => {
  const [category, setCategory] = useState('全部');
  const [searchQ, setSearchQ] = useState('');
  const [detailEvent, setDetailEvent] = useState<SwimEvent | null>(null);
  const [regEvent, setRegEvent] = useState<SwimEvent | null>(null);

  // 报名表单状态
  const [form, setForm] = useState<RegisterFormData>({
    eventId: 0, athleteName: '', idCard: '', phone: '', gender: '男', ageGroup: AGE_GROUPS[1],
    eventItem: EVENT_ITEMS[0], emergencyContact: '', emergencyPhone: '', tshirtSize: 'L', healthDeclared: false,
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (category !== '全部' && e.category !== category) return false;
      if (searchQ && !(e.title + e.location).includes(searchQ)) return false;
      return true;
    });
  }, [events, category, searchQ]);

  const set = (k: keyof RegisterFormData, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const openRegister = (ev: SwimEvent) => {
    if (!user) { onRequireLogin(); return; }
    setFormError('');
    setForm({
      eventId: ev.id, athleteName: user.name, idCard: user.id_card, phone: user.phone,
      gender: '男', ageGroup: AGE_GROUPS[1], eventItem: EVENT_ITEMS[0],
      emergencyContact: '', emergencyPhone: '', tshirtSize: 'L', healthDeclared: false,
    });
    setDetailEvent(null);
    setRegEvent(ev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regEvent) return;
    if (!form.athleteName.trim() || !form.idCard.trim() || !form.phone.trim() || !form.emergencyContact.trim()) {
      setFormError('请补充完整选手姓名、身份证号、手机号及紧急联系人');
      return;
    }
    if (form.idCard.length < 15) { setFormError('请输入合规的18位二代身份证号码'); return; }
    if (!/^1[3-9]\d{9}$/.test(form.phone)) { setFormError('请输入正确的11位手机号'); return; }
    if (!form.healthDeclared) { setFormError('须勾选并同意健康承诺书与参赛声明'); return; }

    setSubmitting(true);
    try {
      await onRegister(regEvent, { ...form });
      setRegEvent(null);
    } catch (ex: any) {
      setFormError(ex.message || '报名提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s: string) => {
    const map: Record<string, { text: string; cls: string }> = {
      upcoming: { text: '报名中', cls: 'bg-emerald-100 text-emerald-800' },
      ongoing: { text: '进行中', cls: 'bg-sky-100 text-sky-800' },
      ended: { text: '已结束', cls: 'bg-slate-200 text-slate-600' },
    };
    const m = map[s] || map.ended;
    return <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${m.cls}`}>{m.text}</span>;
  };

  return (
    <section className="py-12 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 标题 */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-700 bg-sky-100/80 px-3 py-1 rounded-full mb-2">
              <Trophy className="w-3.5 h-3.5 text-sky-600" /><span>长顺水上赛事发布中心</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">游泳赛事与活动报名</h2>
            <p className="text-slate-600 text-sm mt-1">在线注册账号后即可报名，支持微信扫码缴费与后台资格审核。</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-white p-1 rounded-xl shadow-sm border border-slate-200 overflow-x-auto">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${category === c ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-600 hover:text-sky-600 hover:bg-slate-50'}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-slate-200">
              <Search className="w-4 h-4 text-slate-400" />
              <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="搜索赛事/地点" className="bg-transparent text-xs focus:outline-none w-32" />
            </div>
          </div>
        </div>

        {/* 赛事卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((event) => {
            const isFull = event.current_count >= event.max_participants;
            const pct = Math.min(100, Math.round((event.current_count / event.max_participants) * 100));
            return (
              <div key={event.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-shadow flex flex-col">
                <div className="relative h-40 overflow-hidden">
                  {event.cover_image ? (
                    <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-sky-600 to-blue-800 flex items-center justify-center"><Trophy className="w-12 h-12 text-white/40" /></div>
                  )}
                  <div className="absolute top-3 left-3">{statusBadge(event.status)}</div>
                  {event.is_top === 1 && <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500 text-white text-[10px] font-bold rounded-full">置顶</div>}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-sm font-bold text-slate-900 leading-snug mb-2 line-clamp-2">{event.title}</h3>
                  <div className="space-y-1.5 text-xs text-slate-500 mb-4">
                    <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-sky-500 shrink-0" /><span className="truncate">{event.location}</span></p>
                    <p className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-sky-500 shrink-0" />{event.event_date}</p>
                    <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-sky-500 shrink-0" />报名截止 {event.reg_deadline}</p>
                  </div>
                  <div className="mt-auto">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="flex items-center gap-1 text-slate-500"><Users className="w-3.5 h-3.5" />{event.current_count}/{event.max_participants}人</span>
                      <span className="font-black text-sky-700">{event.fee > 0 ? `¥${event.fee}` : '免费'}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
                      <div className={`h-full rounded-full ${isFull ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setDetailEvent(event)}
                        className="flex-1 py-2.5 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 transition-colors">查看详情</button>
                      <button
                        onClick={() => openRegister(event)}
                        disabled={isFull || event.status === 'ended'}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-50 ${isFull || event.status === 'ended' ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-500 hover:to-blue-500 shadow shadow-sky-600/20'}`}>
                        {isFull ? '名额已满' : event.status === 'ended' ? '已结束' : '立即报名'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="col-span-full text-center py-16 text-slate-400 text-sm">暂无符合条件的赛事</div>}
        </div>
      </div>

      {/* 赛事详情弹窗 */}
      {detailEvent && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl relative my-8">
            <button onClick={() => setDetailEvent(null)} className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-3">{statusBadge(detailEvent.status)}<span className="text-xs text-slate-400">{detailEvent.category}</span></div>
            <h3 className="text-xl font-black text-slate-900 mb-4">{detailEvent.title}</h3>
            <div className="space-y-2 text-sm text-slate-600 mb-5">
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-500" />{detailEvent.location}</p>
              <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-sky-500" />{detailEvent.event_date}</p>
              <p className="flex items-center gap-2"><Users className="w-4 h-4 text-sky-500" />已报名 {detailEvent.current_count} / {detailEvent.max_participants} 人 · 报名费 {detailEvent.fee > 0 ? `¥${detailEvent.fee}` : '免费'}</p>
              <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-sky-500" />报名截止：{detailEvent.reg_deadline} · 主办：{detailEvent.organizer}</p>
            </div>
            {detailEvent.description && (
              <div className="mb-4"><h4 className="text-sm font-bold text-slate-900 mb-2">赛事简介</h4><p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{detailEvent.description}</p></div>
            )}
            {detailEvent.rules && (
              <div className="mb-4"><h4 className="text-sm font-bold text-slate-900 mb-2">竞赛规程</h4><p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{detailEvent.rules}</p></div>
            )}
            {detailEvent.requirements && (
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">报名要求</h4>
                <ul className="space-y-1.5">
                  {(typeof detailEvent.requirements === 'string' ? detailEvent.requirements.replace(/^\[|\]$/g, '').split(/","|"\s*,\s*"/) : []).map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600"><AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />{r.replace(/^"|"$/g, '')}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6 pt-5 border-t border-slate-100 flex justify-end">
              <button onClick={() => openRegister(detailEvent)}
                disabled={detailEvent.current_count >= detailEvent.max_participants || detailEvent.status === 'ended'}
                className="px-6 py-3 bg-gradient-to-r from-sky-600 to-blue-600 text-white font-bold text-sm rounded-xl shadow disabled:opacity-50">
                {detailEvent.current_count >= detailEvent.max_participants ? '名额已满' : detailEvent.status === 'ended' ? '已结束' : '立即报名 →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 报名表单弹窗 */}
      {regEvent && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl relative my-8">
            <button onClick={() => setRegEvent(null)} className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-sky-700 bg-sky-100 px-2.5 py-1 rounded">在线报名</span>
              <span className="text-xs text-slate-400">登录用户：{user?.name}</span>
            </div>
            <h3 className="text-lg font-black text-slate-900 mb-5">{regEvent.title}</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">选手姓名 *</label>
                  <input type="text" required value={form.athleteName} onChange={(e) => set('athleteName', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm" />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">身份证号 *</label>
                  <input type="text" required maxLength={18} value={form.idCard} onChange={(e) => set('idCard', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">手机号 *</label>
                  <input type="tel" required maxLength={11} value={form.phone} onChange={(e) => set('phone', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm font-mono" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-bold mb-1 text-xs">性别</label>
                    <select value={form.gender} onChange={(e) => set('gender', e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm">
                      <option>男</option><option>女</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-700 font-bold mb-1 text-xs">年龄组</label>
                    <select value={form.ageGroup} onChange={(e) => set('ageGroup', e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm">
                      {AGE_GROUPS.map((g) => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">参赛项目</label>
                  <select value={form.eventItem} onChange={(e) => set('eventItem', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm">
                    {EVENT_ITEMS.map((it) => <option key={it}>{it}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">紧急联系人姓名 *</label>
                  <input type="text" required value={form.emergencyContact} onChange={(e) => set('emergencyContact', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm" />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">紧急联系人电话 *</label>
                  <input type="tel" required maxLength={11} value={form.emergencyPhone} onChange={(e) => set('emergencyPhone', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1 text-xs">纪念服装尺码</label>
                  <div className="flex gap-1.5">
                    {TSHIRT_SIZES.map((s) => (
                      <button type="button" key={s} onClick={() => set('tshirtSize', s)}
                        className={`flex-1 py-2 rounded-lg border text-xs font-bold transition-all ${form.tshirtSize === s ? 'bg-sky-600 text-white border-sky-600' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl text-[11px] text-amber-800 space-y-1">
                <div className="flex items-center gap-1.5 font-bold text-amber-900"><ShieldAlert className="w-4 h-4 text-amber-600" /><span>长顺县游泳协会 参赛健康与安全承诺书</span></div>
                <p className="leading-relaxed">本人确认身体状况良好，无高血压、心脏病、癫痫等不宜参加剧烈水上运动之疾病。比赛期间严格遵守组委会与裁委会安排，服从救生员指挥。</p>
                <label className="flex items-center gap-2 pt-1 font-bold cursor-pointer text-slate-900">
                  <input type="checkbox" checked={form.healthDeclared} onChange={(e) => set('healthDeclared', e.target.checked)} className="rounded text-sky-600 focus:ring-sky-500 w-4 h-4" />
                  <span>我已阅读并完全同意上述健康与安全声明</span>
                </label>
              </div>

              {formError && <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-1.5"><AlertCircle className="w-4 h-4 shrink-0" />{formError}</p>}

              <div className="pt-2 flex items-center justify-between">
                <div>
                  <span className="text-slate-500 text-xs">实付报名费用:</span>
                  <span className="text-lg font-black text-sky-700 ml-2">¥ {regEvent.fee.toFixed(2)}</span>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setRegEvent(null)} className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm">取消</button>
                  <button type="submit" disabled={submitting}
                    className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-sky-600 hover:from-emerald-500 hover:to-sky-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-600/20 flex items-center gap-1.5 text-sm disabled:opacity-60">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{submitting ? '提交中...' : '确认提交并跳转微信支付'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};
